import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DEFAULT_WIDGET_CONFIG, normalizeWidgetConfig } from '../builder/widgetTypes';
import {
  createWidgetId,
  deleteSavedWidget,
  getSavedWidget,
  listSavedWidgets,
  saveWidget as persistWidget,
} from '../utils/widgetStorage';

const AUTOSAVE_DELAY_MS = 800;

const WidgetBuilderContext = createContext(null);

function configsMatch(savedConfig, currentConfig) {
  return JSON.stringify(savedConfig) === JSON.stringify(currentConfig);
}

export function WidgetBuilderProvider({ children }) {
  const [hydrated, setHydrated] = useState(false);
  const [config, setConfig] = useState({ ...DEFAULT_WIDGET_CONFIG });
  const [widgetId, setWidgetId] = useState(() => createWidgetId());
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [saveMessage, setSaveMessage] = useState(null);
  const [saveStatus, setSaveStatus] = useState('idle');
  const [savedWidgets, setSavedWidgets] = useState([]);
  const [storageMode, setStorageMode] = useState('loading');

  const skipAutosaveRef = useRef(true);

  const refreshSavedList = useCallback(async () => {
    try {
      const list = await listSavedWidgets();
      setSavedWidgets(list);
      setStorageMode('cloud');
      return list;
    } catch {
      setStorageMode('local');
      const list = await listSavedWidgets();
      setSavedWidgets(list);
      return list;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const list = await listSavedWidgets();
        if (cancelled) return;

        setSavedWidgets(list);
        setStorageMode('cloud');

        if (list.length > 0) {
          const latest = list[0];
          setWidgetId(latest.id);
          setConfig({ ...DEFAULT_WIDGET_CONFIG, ...normalizeWidgetConfig(latest.config) });
          setLastSavedAt(latest.updatedAt);
          setSaveStatus('saved');
        }
      } catch {
        if (!cancelled) setStorageMode('local');
      } finally {
        if (!cancelled) {
          setHydrated(true);
          skipAutosaveRef.current = true;
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const updateConfig = useCallback((partial) => {
    setConfig((prev) => ({ ...prev, ...partial }));
    setSaveMessage(null);
  }, []);

  const saveCurrentWidget = useCallback(async () => {
    try {
      const entry = await persistWidget(widgetId, config);
      if (entry.config) {
        setConfig((prev) => ({ ...prev, ...normalizeWidgetConfig(entry.config) }));
      }
      setLastSavedAt(entry.updatedAt);
      setSaveMessage('saved');
      setSaveStatus('saved');
      setStorageMode('cloud');
      await refreshSavedList();
      return entry;
    } catch {
      setSaveMessage('error');
      setSaveStatus('error');
      return null;
    }
  }, [widgetId, config, refreshSavedList]);

  const startNewWidget = useCallback(() => {
    skipAutosaveRef.current = true;
    const id = createWidgetId();
    setWidgetId(id);
    setConfig({ ...DEFAULT_WIDGET_CONFIG });
    setLastSavedAt(null);
    setSaveMessage(null);
    setSaveStatus('idle');
    return id;
  }, []);

  const loadWidgetById = useCallback((id) => {
    const entry = savedWidgets.find((w) => w.id === id);
    if (!entry) return false;
    skipAutosaveRef.current = true;
    setWidgetId(entry.id);
    setConfig({ ...DEFAULT_WIDGET_CONFIG, ...normalizeWidgetConfig(entry.config) });
    setLastSavedAt(entry.updatedAt);
    setSaveMessage(null);
    setSaveStatus('saved');
    return true;
  }, [savedWidgets]);

  const removeWidgetById = useCallback(
    async (id) => {
      await deleteSavedWidget(id);
      const remaining = await refreshSavedList();

      if (id !== widgetId) return;

      if (remaining.length > 0) {
        const next = remaining[0];
        skipAutosaveRef.current = true;
        setWidgetId(next.id);
        setConfig({ ...DEFAULT_WIDGET_CONFIG, ...normalizeWidgetConfig(next.config) });
        setLastSavedAt(next.updatedAt);
        setSaveStatus('saved');
      } else {
        skipAutosaveRef.current = true;
        const newId = createWidgetId();
        setWidgetId(newId);
        setConfig({ ...DEFAULT_WIDGET_CONFIG });
        setLastSavedAt(null);
        setSaveStatus('idle');
      }
      setSaveMessage(null);
    },
    [widgetId, refreshSavedList]
  );

  useEffect(() => {
    if (!hydrated) return;

    if (skipAutosaveRef.current) {
      skipAutosaveRef.current = false;
      return;
    }

    const saved = savedWidgets.find((w) => w.id === widgetId);
    const isDirty = !saved || !configsMatch(saved.config, config);

    if (!isDirty) {
      setSaveStatus('saved');
      return;
    }

    setSaveStatus('pending');

    const timer = window.setTimeout(async () => {
      setSaveStatus('saving');
      try {
        const entry = await persistWidget(widgetId, config);
        if (entry.config) {
          setConfig((prev) => ({ ...prev, ...normalizeWidgetConfig(entry.config) }));
        }
        setLastSavedAt(entry.updatedAt);
        setStorageMode('cloud');
        await refreshSavedList();
        setSaveStatus('saved');
        setSaveMessage(null);
      } catch {
        setSaveStatus('error');
        setSaveMessage('error');
      }
    }, AUTOSAVE_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [config, widgetId, savedWidgets, hydrated, refreshSavedList]);

  const isUnsavedDraft = !savedWidgets.some((w) => w.id === widgetId);

  const value = useMemo(
    () => ({
      hydrated,
      storageMode,
      config,
      updateConfig,
      widgetId,
      lastSavedAt,
      saveMessage,
      setSaveMessage,
      saveStatus,
      savedWidgets,
      isUnsavedDraft,
      refreshSavedList,
      saveCurrentWidget,
      startNewWidget,
      loadWidgetById,
      removeWidgetById,
    }),
    [
      hydrated,
      storageMode,
      config,
      updateConfig,
      widgetId,
      lastSavedAt,
      saveMessage,
      saveStatus,
      savedWidgets,
      isUnsavedDraft,
      refreshSavedList,
      saveCurrentWidget,
      startNewWidget,
      loadWidgetById,
      removeWidgetById,
    ]
  );

  if (!hydrated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-sm text-slate-500">Loading widgets…</p>
      </div>
    );
  }

  return (
    <WidgetBuilderContext.Provider value={value}>{children}</WidgetBuilderContext.Provider>
  );
}

export function useWidgetBuilder() {
  const ctx = useContext(WidgetBuilderContext);
  if (!ctx) {
    throw new Error('useWidgetBuilder must be used inside WidgetBuilderProvider');
  }
  return ctx;
}
